import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
} from '@mui/material';
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExportContainer,
  GridToolbarQuickFilter,
  GridCsvExportMenuItem,
} from '@mui/x-data-grid-pro';
import {
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Home as HomeIcon,
  Flag as FlagIcon,
  Star as StarIcon,
  PersonAdd as PersonAddIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { BarChart } from '@mui/x-charts/BarChart';
import salesforceService from '../services/salesforceService';
import indexedDBService from '../services/indexedDBService';
import { fetchCeoWpPropertyPostsFy } from '../services/ceoReportingService';
import { useDateRange } from '../context/DateContext';
import dayjs from 'dayjs';

const numFmt = new Intl.NumberFormat('en-AU');

function formatFyDelta(current, previous) {
  if (previous == null || current == null) return null;
  const c = Number(current);
  const p = Number(previous);
  const diff = c - p;
  if (Number.isNaN(diff)) return null;
  const pct = p !== 0 && Number.isFinite(p) ? ((diff / p) * 100).toFixed(1) : null;
  const arrow = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  return {
    text: `${diff >= 0 ? '+' : ''}${numFmt.format(diff)}${pct != null ? ` (${pct}%)` : ''}`,
    arrow,
  };
}

const CustomToolbar = () => {
  return (
    <GridToolbarContainer sx={{ flexWrap: 'wrap', justifyContent: 'flex-start' }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExportContainer>
        <GridCsvExportMenuItem options={{
          fileName: 'onboarding_export',
          utf8WithBom: true,
        }} />
      </GridToolbarExportContainer>
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ minWidth: '320px' }}>
        <GridToolbarQuickFilter />
      </Box>
    </GridToolbarContainer>
  );
};

const Onboarding = () => {
  const { startDate, endDate } = useDateRange();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Section data states
  const [locationStages, setLocationStages] = useState([]);
  const [popularLocations, setPopularLocations] = useState([]);
  const [redFlags, setRedFlags] = useState([]);
  const [registrationStats, setRegistrationStats] = useState([]);
  const [exclusiveLocations, setExclusiveLocations] = useState({ count: 0, locations: [] });
  const [untouchedLocations, setUntouchedLocations] = useState([]);
  const [potentialLocations, setPotentialLocations] = useState([]);
  const [totalLocations, setTotalLocations] = useState(0);
  const [wpPropertyPostsFy, setWpPropertyPostsFy] = useState(null);
  const [wpPropertyPostsError, setWpPropertyPostsError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setWpPropertyPostsError(null);
      
      // Load all data in parallel
      const [
        stages,
        redFlagData,
        exclusiveData,
        registrationData,
        untouchedData,
        potentialData,
        popularLocationsData,
        allLocations,
        wpPropertyPostsData,
      ] = await Promise.all([
        salesforceService.getLeadStagesWithAverageTime(),
        salesforceService.getRedFlagLocations(),
        salesforceService.getExclusiveLocations(),
        salesforceService.getRegistrationStatsByState(startDate.toDate(), endDate.toDate()),
        salesforceService.getUntouchedLocations(),
        salesforceService.getPotentialLocations(),
        salesforceService.getPopularLocationsComparison(),
        indexedDBService.getAllLocations(),
        fetchCeoWpPropertyPostsFy().catch((error) => {
          console.error('Error loading WordPress property posts FY data:', error);
          setWpPropertyPostsError(error.response?.data?.details || error.message || 'Failed to load property posts');
          return null;
        }),
      ]);

      setLocationStages(stages);
      setRedFlags(redFlagData);
      setExclusiveLocations(exclusiveData);
      
      // Debug registration stats
      console.log('DEBUG: Registration stats received:', registrationData.length, 'records');
      if (registrationData.length > 0) {
        console.log('DEBUG: First registration stat:', registrationData[0]);
      }
      
      setRegistrationStats(registrationData);
      setUntouchedLocations(untouchedData);
      setPotentialLocations(potentialData);
      setPopularLocations(popularLocationsData);
      setTotalLocations(allLocations.length);
      setWpPropertyPostsFy(wpPropertyPostsData);

    } catch (error) {
      console.error('Error loading onboarding data:', error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const openSalesforceRecord = (recordId, isLead = false) => {
    const sfBaseUrl = process.env.REACT_APP_SALESFORCE_URL || 'https://purelocations.lightning.force.com';
    const objectType = isLead ? 'Lead' : 'Account';
    const sfUrl = `${sfBaseUrl}/lightning/r/${objectType}/${recordId}/view`;
    window.open(sfUrl, '_blank');
  };

  // Registration stats chart data
  const registrationChartData = useMemo(() => {
    if (!registrationStats || registrationStats.length === 0) {
      return { months: [], states: [], data: {} };
    }
    
    const monthlyData = {};
    registrationStats.forEach(stat => {
      if (!stat.year || !stat.month || !stat.state) return;
      const key = `${stat.year}-${String(stat.month).padStart(2, '0')}`;
      if (!monthlyData[key]) {
        monthlyData[key] = {};
      }
      monthlyData[key][stat.state] = (monthlyData[key][stat.state] || 0) + stat.count;
    });

    const months = Object.keys(monthlyData).sort().slice(-12); // Last 12 months
    const states = [...new Set(registrationStats.map(s => s.state).filter(Boolean))].sort();

    console.log('DEBUG: Registration chart data:', {
      monthsCount: months.length,
      statesCount: states.length,
      months: months.slice(-6),
      states: states.slice(0, 5)
    });

    return {
      months,
      states,
      data: monthlyData
    };
  }, [registrationStats]);

  const wpPropertyChartData = useMemo(() => {
    if (!wpPropertyPostsFy?.currentFY?.monthly?.length) {
      return { months: [], currentSeries: [], previousSeries: [] };
    }
    const months = wpPropertyPostsFy.currentFY.monthly.map((row) => row.month);
    const previousByMonth = new Map(
      (wpPropertyPostsFy.previousFY?.monthly || []).map((row) => [row.month, row.count])
    );
    return {
      months,
      currentSeries: wpPropertyPostsFy.currentFY.monthly.map((row) => row.count),
      previousSeries: months.map((month) => previousByMonth.get(month) || 0),
    };
  }, [wpPropertyPostsFy]);

  const wpPropertyDelta = useMemo(() => {
    if (!wpPropertyPostsFy) return null;
    return formatFyDelta(
      wpPropertyPostsFy.currentFY?.publishedCount,
      wpPropertyPostsFy.previousFY?.publishedCount
    );
  }, [wpPropertyPostsFy]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Onboarding Dashboard
        </Typography>
        <IconButton onClick={handleRefresh} disabled={refreshing} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        {/* ========== TOP ROW: Overview Metrics ========== */}
        
        {/* Location Lead Stage Tracker */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssignmentIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Location Lead Stage Tracker</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {locationStages.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Stage</strong></TableCell>
                        <TableCell align="right"><strong>Count</strong></TableCell>
                        <TableCell align="right"><strong>Avg Days</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {locationStages.map((stage, idx) => (
                        <TableRow 
                          key={idx}
                          onClick={() => {
                            if (stage.count > 0) {
                              navigate(`/registrations?status=${encodeURIComponent(stage.status)}`);
                            }
                          }}
                          sx={{
                            cursor: stage.count > 0 ? 'pointer' : 'default',
                            '&:hover': stage.count > 0 ? {
                              backgroundColor: 'action.hover'
                            } : {}
                          }}
                        >
                          <TableCell>{stage.status}</TableCell>
                          <TableCell align="right">
                            <Chip label={stage.count} color="primary" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            {stage.count > 0 ? (
                              <Chip 
                                label={`${stage.averageDaysInStage} days`} 
                                color={stage.averageDaysInStage > 30 ? 'error' : stage.averageDaysInStage > 14 ? 'warning' : 'default'} 
                                size="small" 
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">No stage data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Exclusive Listings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StarIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Exclusive Listings</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="h3" color="primary">
                  {exclusiveLocations.count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {totalLocations > 0 
                    ? `${((exclusiveLocations.count / totalLocations) * 100).toFixed(1)}% of total locations`
                    : 'Total locations'}
                </Typography>
              </Box>
              {exclusiveLocations.locations.length > 0 && (
                <Box sx={{ 
                  maxHeight: 400, 
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0,0,0,0.05)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.3)',
                    },
                  },
                }}>
                  {exclusiveLocations.locations.map((loc) => (
                    <Box key={loc.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
                        {loc.name}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => openSalesforceRecord(loc.id)}
                        title="Open in Salesforce"
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ========== SECOND ROW: Critical Action Items ========== */}
        
        {/* Red Flags */}
        <Grid item xs={12}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FlagIcon sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">Red Flags</Typography>
                <Chip 
                  label={redFlags.length} 
                  color="error" 
                  size="small" 
                  sx={{ ml: 2 }} 
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              {redFlags.length > 0 ? (
                <Box sx={{ height: 400, flexGrow: 1 }}>
                  <DataGridPro
                    rows={redFlags.map((flag) => ({
                      id: flag.id,
                      name: flag.name || 'N/A',
                      issues: flag.issues || 'N/A',
                      lastModified: flag.lastModified, // Use directly from service response
                      address: flag.address || 'N/A'
                    }))}
                    columns={[
                      { field: 'name', headerName: 'Location', flex: 1, minWidth: 200 },
                      { 
                        field: 'issues', 
                        headerName: 'Issue', 
                        flex: 2, 
                        minWidth: 300,
                        renderCell: (params) => (
                          <Tooltip title={params.value || ''}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {params.value || 'N/A'}
                            </Typography>
                          </Tooltip>
                        )
                      },
                      { 
                        field: 'lastModified', 
                        headerName: 'Last Modified', 
                        width: 150,
                        type: 'dateTime',
                        valueGetter: (value, row) => {
                          // Get the date value from the row - Salesforce returns ISO strings like "2026-02-03T01:32:31.000+0000"
                          const dateValue = row.lastModified;
                          if (!dateValue) return null;
                          try {
                            // Convert ISO string to Date object
                            const date = new Date(dateValue);
                            // Check if date is valid
                            if (isNaN(date.getTime())) {
                              console.warn('Invalid date value:', dateValue);
                              return null;
                            }
                            return date;
                          } catch (e) {
                            console.error('Error parsing date:', dateValue, e);
                            return null;
                          }
                        },
                        valueFormatter: (params) => {
                          if (!params.value) return 'N/A';
                          try {
                            // params.value should be a Date object from valueGetter
                            // But handle both Date objects and strings
                            const date = params.value instanceof Date ? params.value : new Date(params.value);
                            if (isNaN(date.getTime())) {
                              return 'N/A';
                            }
                            return dayjs(date).format('DD/MM/YYYY');
                          } catch (e) {
                            console.error('Error formatting date:', params.value, e);
                            return 'N/A';
                          }
                        },
                        renderCell: (params) => {
                          // Use renderCell instead of valueFormatter for more control
                          const dateValue = params.row.lastModified;
                          if (!dateValue) return 'N/A';
                          try {
                            const date = new Date(dateValue);
                            if (isNaN(date.getTime())) return 'N/A';
                            return dayjs(date).format('DD/MM/YYYY');
                          } catch (e) {
                            return 'N/A';
                          }
                        }
                      },
                      {
                        field: 'actions',
                        headerName: 'Actions',
                        width: 100,
                        sortable: false,
                        renderCell: (params) => (
                          <IconButton 
                            size="small" 
                            onClick={() => openSalesforceRecord(params.row.id)}
                            title="Open in Salesforce"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        )
                      }
                    ]}
                    slots={{ toolbar: CustomToolbar }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 25 } }
                    }}
                  />
                </Box>
              ) : (
                <Alert severity="success">No red flags found!</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ========== THIRD ROW: Performance & Trends ========== */}
        
        {/* Popular Locations Comparison */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Popular Locations Comparison</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Last 3 months vs same period last year
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {popularLocations.length > 0 ? (
                <Box sx={{ height: 350, flexGrow: 1 }}>
                  <DataGridPro
                    rows={popularLocations.map((loc) => ({
                      id: loc.locationId || loc.name, // Use locationId or name as unique ID
                      ...loc
                    }))}
                    columns={[
                      { field: 'name', headerName: 'Location Name', flex: 1, minWidth: 200 },
                      { 
                        field: 'lastYearBookings', 
                        headerName: 'Last Year', 
                        width: 120,
                        type: 'number'
                      },
                      { 
                        field: 'thisYearBookings', 
                        headerName: 'This Year', 
                        width: 120,
                        type: 'number'
                      },
                      { 
                        field: 'daysSinceLastBooking', 
                        headerName: 'Days Since', 
                        width: 130,
                        type: 'number',
                        renderCell: (params) => (
                          <Chip 
                            label={params.value || 'N/A'} 
                            color={params.value && params.value > 90 ? 'error' : 'default'}
                            size="small"
                          />
                        )
                      },
                      {
                        field: 'lastBookingDate',
                        headerName: 'Last Booking',
                        width: 130,
                        renderCell: (params) => {
                          const dateValue = params.row.lastBookingDate;
                          if (!dateValue) return 'N/A';
                          try {
                            const date = new Date(dateValue);
                            if (isNaN(date.getTime())) return 'N/A';
                            return dayjs(date).format('DD/MM/YYYY');
                          } catch (e) {
                            return 'N/A';
                          }
                        }
                      }
                    ]}
                    slots={{ toolbar: CustomToolbar }}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } }
                    }}
                  />
                </Box>
              ) : (
                <Alert severity="info">No comparison data available</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Registration Stats by State */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Registration Stats by State</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontStyle: 'italic' }}>
                Uses date range specified in the sidebar
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {registrationChartData.months.length > 0 && registrationChartData.states.length > 0 ? (
                <Box sx={{ height: 350 }}>
                  <BarChart
                    xAxis={[{
                      data: registrationChartData.months,
                      scaleType: 'band',
                    }]}
                    series={registrationChartData.states.slice(0, 5).map(state => ({
                      label: state,
                      data: registrationChartData.months.map(month => 
                        registrationChartData.data[month]?.[state] || 0
                      )
                    }))}
                    height={350}
                  />
                </Box>
              ) : (
                <Typography color="text.secondary">No registration stats available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ========== FOURTH ROW: Management & Pipeline ========== */}
        
        {/* Untouched Locations */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Untouched Locations</Typography>
                <Chip 
                  label={untouchedLocations.length} 
                  color="warning" 
                  size="small" 
                  sx={{ ml: 2 }} 
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              {untouchedLocations.length > 0 ? (
                <Box sx={{ height: 350, flexGrow: 1 }}>
                  <DataGridPro
                    rows={untouchedLocations.map((loc, idx) => ({
                      id: idx,
                      ...loc
                    }))}
                    columns={[
                      { field: 'name', headerName: 'Location Name', flex: 1, minWidth: 200 },
                      { field: 'address', headerName: 'Address', flex: 1, minWidth: 200 },
                      { 
                        field: 'hasEnquiries', 
                        headerName: 'Has Enquiries', 
                        width: 120,
                        renderCell: (params) => (
                          <Chip 
                            label={params.value ? 'Yes' : 'No'} 
                            color={params.value ? 'success' : 'error'}
                            size="small"
                          />
                        )
                      },
                      { 
                        field: 'daysSinceActivity', 
                        headerName: 'Days Since Activity', 
                        width: 150,
                        type: 'number',
                        renderCell: (params) => (
                          <Chip 
                            label={params.value} 
                            color={params.value > 180 ? 'error' : params.value > 90 ? 'warning' : 'default'}
                            size="small"
                          />
                        )
                      },
                      {
                        field: 'lastModified',
                        headerName: 'Last Modified',
                        width: 150,
                        type: 'dateTime',
                        valueGetter: (value, row) => {
                          // Get the date value from the row - Salesforce returns ISO strings
                          const dateValue = row.lastModified;
                          if (!dateValue) return null;
                          try {
                            // Convert ISO string to Date object
                            const date = new Date(dateValue);
                            // Check if date is valid
                            if (isNaN(date.getTime())) {
                              console.warn('Invalid date value:', dateValue);
                              return null;
                            }
                            return date;
                          } catch (e) {
                            console.warn('Error parsing date:', dateValue, e);
                            return null;
                          }
                        },
                        renderCell: (params) => {
                          const dateValue = params.row.lastModified;
                          if (!dateValue) return 'N/A';
                          try {
                            const date = new Date(dateValue);
                            if (isNaN(date.getTime())) return 'N/A';
                            return dayjs(date).format('DD/MM/YYYY');
                          } catch (e) {
                            return 'N/A';
                          }
                        }
                      },
                      {
                        field: 'actions',
                        headerName: 'Actions',
                        width: 120,
                        sortable: false,
                        renderCell: (params) => (
                          <IconButton 
                            size="small" 
                            onClick={() => openSalesforceRecord(params.row.id)}
                            title="Open in Salesforce"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        )
                      }
                    ]}
                    slots={{ toolbar: CustomToolbar }}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } }
                    }}
                  />
                </Box>
              ) : (
                <Alert severity="success">All locations have recent activity!</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Potential Locations Tracker */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonAddIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Potential Locations Tracker</Typography>
                <Chip 
                  label={potentialLocations.length} 
                  color="primary" 
                  size="small" 
                  sx={{ ml: 2 }} 
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              {potentialLocations.length > 0 ? (
                <Box sx={{ height: 350, flexGrow: 1 }}>
                  <DataGridPro
                    rows={potentialLocations.map((loc, idx) => ({
                      id: idx,
                      ...loc
                    }))}
                    columns={[
                      { field: 'name', headerName: 'Name/Company', flex: 1, minWidth: 150 },
                      { field: 'company', headerName: 'Company', flex: 1, minWidth: 120 },
                      { field: 'status', headerName: 'Status', width: 100 },
                      { field: 'owner', headerName: 'Owner', width: 120 },
                      { field: 'email', headerName: 'Email', flex: 1, minWidth: 150 },
                      { field: 'phone', headerName: 'Phone', width: 100 },
                      {
                        field: 'createdDate',
                        headerName: 'Created',
                        width: 110,
                        valueFormatter: (params) => 
                          params.value ? dayjs(params.value).format('DD/MM/YYYY') : 'N/A'
                      },
                      {
                        field: 'description',
                        headerName: 'Description',
                        flex: 1,
                        minWidth: 150,
                        renderCell: (params) => (
                          <Tooltip title={params.value || ''}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                maxWidth: 200, 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {params.value || 'N/A'}
                            </Typography>
                          </Tooltip>
                        )
                      },
                      {
                        field: 'actions',
                        headerName: 'Actions',
                        width: 100,
                        sortable: false,
                        renderCell: (params) => (
                          <IconButton 
                            size="small" 
                            onClick={() => openSalesforceRecord(params.row.id, true)}
                            title="Open in Salesforce"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        )
                      }
                    ]}
                    slots={{ toolbar: CustomToolbar }}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } }
                    }}
                  />
                </Box>
              ) : (
                <Alert severity="info">No potential locations found. Leads with RecordType "Location Scout" will appear here.</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ========== FIFTH ROW: Website & Additional Information ========== */}

        {/* Property posts on website — FY comparison */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LanguageIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Property Posts on Website</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Published property posts — current financial year vs same period last year
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {wpPropertyPostsError ? (
                <Alert severity="error">{wpPropertyPostsError}</Alert>
              ) : wpPropertyPostsFy ? (
                <>
                  {wpPropertyPostsFy.authWarning && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {wpPropertyPostsFy.authWarning}
                    </Alert>
                  )}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="overline" color="text.secondary">
                          {wpPropertyPostsFy.currentFY?.label || 'This FY'}
                        </Typography>
                        <Typography variant="h3" color="primary">
                          {numFmt.format(wpPropertyPostsFy.currentFY?.publishedCount || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          YTD published
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="overline" color="text.secondary">
                          {wpPropertyPostsFy.previousFY?.label || 'Last FY'}
                        </Typography>
                        <Typography variant="h3" color="text.secondary">
                          {numFmt.format(wpPropertyPostsFy.previousFY?.publishedCount || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Same period last year
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  {wpPropertyDelta && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <Chip
                        icon={
                          wpPropertyDelta.arrow === 'up' ? (
                            <TrendingUpIcon />
                          ) : wpPropertyDelta.arrow === 'down' ? (
                            <TrendingDownIcon />
                          ) : (
                            <TrendingFlatIcon />
                          )
                        }
                        label={wpPropertyDelta.text}
                        color={
                          wpPropertyDelta.arrow === 'up'
                            ? 'success'
                            : wpPropertyDelta.arrow === 'down'
                              ? 'error'
                              : 'default'
                        }
                        size="small"
                      />
                    </Box>
                  )}
                  {wpPropertyChartData.months.length > 0 ? (
                    <Box sx={{ height: 280 }}>
                      <BarChart
                        xAxis={[{
                          data: wpPropertyChartData.months,
                          scaleType: 'band',
                        }]}
                        series={[
                          {
                            label: wpPropertyPostsFy.currentFY?.label || 'This FY',
                            data: wpPropertyChartData.currentSeries,
                          },
                          {
                            label: wpPropertyPostsFy.previousFY?.label || 'Last FY',
                            data: wpPropertyChartData.previousSeries,
                          },
                        ]}
                        height={280}
                      />
                    </Box>
                  ) : (
                    <Typography color="text.secondary">No monthly breakdown available</Typography>
                  )}
                </>
              ) : (
                <Typography color="text.secondary">No property post data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Homeowner Dashboard Statistics (Placeholder) */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HomeIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Homeowner Dashboard Statistics</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="info" sx={{ mb: 2 }}>
                Homeowner dashboard statistics coming soon. This section will display
                metrics and insights related to homeowner engagement and activity.
              </Alert>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Locations: <strong>{totalLocations}</strong>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Onboarding;
